import { sortDiscontinuedLast } from './discontinued';
import { normalizeCode, normalizeSearchText } from './normalize';
import type { Catalog, Category, LocaleCode, Product, SubcategoryPage } from './types';
import { isProductParent } from './variants';

export type CatalogSearchHit =
  | { type: 'product'; product: Product; reason: 'code' | 'text' | 'alias' }
  | { type: 'category'; category: Category; reason: 'text' | 'alias' }
  | { type: 'subcategory'; subcategory: SubcategoryPage; reason: 'text' | 'alias' };

export type CatalogSearchResult = {
  query: string;
  locale: LocaleCode;
  /** Parent SKUs for alias/text hits; exact code may return a colour child. */
  products: Product[];
  categories: Category[];
  subcategories: SubcategoryPage[];
  hits: CatalogSearchHit[];
};

type TranslateFn = (key: string) => string;

/**
 * Local list filter: name / code / short code (`ja-103k` ≡ `ja103k`).
 * Same separator-insensitive rules as `searchCatalog` code matching.
 */
export function matchesProductQuery(product: Product, query: string, t: TranslateFn = (key) => key): boolean {
  const trimmed = query.trim();
  if (!trimmed) {
    return true;
  }

  const needleNorm = normalizeSearchText(trimmed);
  const needleCode = normalizeCode(trimmed);
  if (!needleNorm) {
    return true;
  }

  const codeNorm = normalizeCode(product.code);
  const idNorm = normalizeCode(product.id);
  if (
    needleCode.length >= 2 &&
    (codeNorm === needleCode || idNorm === needleCode || codeNorm.includes(needleCode) || idNorm.includes(needleCode))
  ) {
    return true;
  }

  return textMatches(t(product.name), needleNorm) || textMatches(t(product.desc), needleNorm);
}

function aliasesFor(locale: LocaleCode, aliases?: Partial<Record<LocaleCode, string[]>>): string[] {
  return aliases?.[locale] ?? [];
}

function textMatches(haystack: string, needleNorm: string): boolean {
  if (!haystack || !needleNorm) {
    return false;
  }

  return normalizeSearchText(haystack).includes(needleNorm);
}

function anyAliasMatches(aliases: string[], needleNorm: string): boolean {
  return aliases.some((alias) => textMatches(alias, needleNorm));
}

/**
 * Client-side catalog search (CS/EN).
 * - Locale-scoped aliases only (CS “pirko” never matches on EN page).
 * - Code match ignores case / dashes / spaces (`ja108k` ≡ `JA-108K`).
 * - Diacritics ignored (`sbernice` ≡ `sběrnice`).
 * - Categories: title + locale aliases only (not description / slug).
 * - Alias/text hits on a colour child collapse to the family parent.
 * - Does not cross languages.
 */
export function searchCatalog(
  catalog: Catalog,
  query: string,
  locale: LocaleCode,
  t: TranslateFn = (key) => key,
): CatalogSearchResult {
  const trimmed = query.trim();
  const empty: CatalogSearchResult = {
    query: trimmed,
    locale,
    products: [],
    categories: [],
    subcategories: [],
    hits: [],
  };
  if (!trimmed) {
    return empty;
  }

  const needleNorm = normalizeSearchText(trimmed);
  const needleCode = normalizeCode(trimmed);
  const hits: CatalogSearchHit[] = [];
  const productIds = new Set<string>();
  const categoryIds = new Set<string>();
  const subcategoryIds = new Set<string>();

  const byId = new Map(catalog.products.map((p) => [p.id, p]));

  for (const product of catalog.products) {
    const codeNorm = normalizeCode(product.code);
    const idNorm = normalizeCode(product.id);
    if (codeNorm === needleCode || idNorm === needleCode || codeNorm.includes(needleCode)) {
      if (needleCode.length >= 3 || codeNorm === needleCode || idNorm === needleCode) {
        if (!productIds.has(product.id)) {
          productIds.add(product.id);
          hits.push({ type: 'product', product, reason: 'code' });
        }
        continue;
      }
    }

    const aliasHit = anyAliasMatches(aliasesFor(locale, product.searchAliases), needleNorm);
    if (aliasHit) {
      const parent = isProductParent(product) ? product : byId.get(product.parentId as string) ?? product;
      if (!productIds.has(parent.id)) {
        productIds.add(parent.id);
        hits.push({ type: 'product', product: parent, reason: 'alias' });
      }
      continue;
    }

    const name = t(product.name);
    const desc = t(product.desc);
    if (textMatches(name, needleNorm) || textMatches(desc, needleNorm) || textMatches(product.code, needleNorm)) {
      const parent = isProductParent(product) ? product : byId.get(product.parentId as string) ?? product;
      // Exact-ish name/code already handled; text hits prefer parent for family browse.
      if (!productIds.has(parent.id)) {
        productIds.add(parent.id);
        hits.push({ type: 'product', product: parent, reason: 'text' });
      }
    }
  }

  for (const category of catalog.categories) {
    const title = t(category.title);
    const aliasHit = anyAliasMatches(aliasesFor(locale, category.searchAliases), needleNorm);
    if (aliasHit || textMatches(title, needleNorm)) {
      if (!categoryIds.has(category.id)) {
        categoryIds.add(category.id);
        hits.push({
          type: 'category',
          category,
          reason: aliasHit ? 'alias' : 'text',
        });
      }
    }
  }

  for (const subcategory of catalog.subcategoryPages) {
    const title = t(subcategory.title);
    const subtitle = subcategory.subtitle ? t(subcategory.subtitle) : '';
    const aliasHit = anyAliasMatches(aliasesFor(locale, subcategory.searchAliases), needleNorm);
    if (aliasHit || textMatches(title, needleNorm) || textMatches(subtitle, needleNorm)) {
      if (!subcategoryIds.has(subcategory.id)) {
        subcategoryIds.add(subcategory.id);
        hits.push({
          type: 'subcategory',
          subcategory,
          reason: aliasHit ? 'alias' : 'text',
        });
      }
    }
  }

  // Expand category / subcategory hits into product list (parents preferred).
  for (const hit of hits) {
    if (hit.type === 'category') {
      const subIds = new Set(catalog.subcategoryPages.filter((s) => s.categoryId === hit.category.id).map((s) => s.id));
      for (const product of catalog.products) {
        if (!product.subcategoryIds.some((id) => subIds.has(id))) {
          continue;
        }
        const parent = isProductParent(product) ? product : byId.get(product.parentId as string) ?? product;
        if (!productIds.has(parent.id)) {
          productIds.add(parent.id);
        }
      }
    }
    if (hit.type === 'subcategory') {
      for (const product of catalog.products) {
        if (!product.subcategoryIds.includes(hit.subcategory.id)) {
          continue;
        }
        const parent = isProductParent(product) ? product : byId.get(product.parentId as string) ?? product;
        if (!productIds.has(parent.id)) {
          productIds.add(parent.id);
        }
      }
    }
  }

  return {
    query: trimmed,
    locale,
    products: sortDiscontinuedLast([...productIds].map((id) => byId.get(id)).filter((p): p is Product => !!p)),
    categories: catalog.categories.filter((c) => categoryIds.has(c.id)),
    subcategories: catalog.subcategoryPages.filter((s) => subcategoryIds.has(s.id)),
    hits,
  };
}
